import {
  Button,
  Grid,
  Loader,
  ScrollView,
  SearchField,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";

import { useContext, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import moment from "moment";
import {
  CCard,
  CCardBody,
  CCollapse,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CTable,
} from "@coreui/react";

import { maintenanceRoutinesByCompanyID } from "graphql/queries";
import { updateMaintenanceRoutine } from "graphql/mutations";
import usePermissions from "hooks/usePermission";
import { MaintenanceRoutineCreateForm, MaintenanceRoutineUpdateForm } from ".";
import CompanyBranchFilter from "components/companies/CompanyBranchFilter";
import { LayoutContext } from "layout/context/LayoutContext";
import { getCompanyName } from "graphql/custom-queries";
import ConfirmModal from "components/utils/ConfirmModal";
import ActionIcon from "components/actions/ActionIcon";
import Activities from "./activities/Activities";

const MaintenanceRoutines = () => {
  const { me } = useContext(LayoutContext);
  const [canCreate, canUpdate, canDelete, canReadActs] = usePermissions([
    "maintenance-routine:create",
    "maintenance-routine:update",
    "maintenance-routine:delete",
    "maintenance-routine:read-act",
  ]);
  const [maintenanceRoutines, setMaintenanceRoutines] = useState([]);
  const [maintenanceRoutinesLoading, setMaintenanceRoutinesLoading] =
    useState(false);
  const [maintenanceRoutineDetails, setMaintenanceRoutineDetails] = useState();
  const [paginationTokens, setPaginationTokens] = useState({
    previous: [],
    nextToken: null,
  });
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    maintenanceRoutineId: null,
    maintenanceRoutineName: null,
  });
  const [modalMaintenanceRoutine, setModalMaintenanceRoutine] = useState({
    visible: false,
    maintenanceRoutineId: null,
  });
  const [modalActs, setModalActs] = useState({
    visible: false,
    maintenanceRoutineId: null,
  });
  const [searchBy, setSearchBy] = useState({
    company: {
      id: "",
      display: "",
    },
    branch: {
      id: "",
      display: "",
    },
  });
  const columnsMapped = useMemo(
    () =>
      maintenanceRoutines.map((item) => ({
        ...item,
        companyName: item.companyName || <Loader />,
        actions: (
          <>
            <ActionIcon
              name={
                maintenanceRoutineDetails?.id === item.id
                  ? strings.maintenanceRoutines.hide
                  : strings.maintenanceRoutines.show
              }
              icon={
                maintenanceRoutineDetails?.id === item.id
                  ? "VisibilityOff"
                  : "Visibility"
              }
              color="primary"
              onClick={() => {
                maintenanceRoutineDetails?.id === item.id
                  ? setMaintenanceRoutineDetails(null)
                  : setMaintenanceRoutineDetails(item);
              }}
            />
            {canUpdate && (
              <ActionIcon
                name={strings.maintenanceRoutines.edit}
                icon="Edit"
                color="primary"
                onClick={() => openModal("update", item.id)}
              />
            )}
            {canReadActs && (
              <ActionIcon
                name={strings.maintenanceRoutines.acts}
                icon="AccountTree"
                color="primary"
                onClick={() => {
                  setModalActs({
                    visible: true,
                    maintenanceRoutineId: item.id,
                    maintenanceRoutineName: item.name,
                  });
                }}
              />
            )}
            {canDelete && (
              <ActionIcon
                name={strings.maintenanceRoutines.delete}
                icon="Delete"
                color="red.60"
                onClick={() => {
                  setModalDetails({
                    visible: true,
                    maintenanceRoutineId: item.id,
                    maintenanceRoutineName: item.name,
                  });
                }}
              />
            )}
          </>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [maintenanceRoutines, maintenanceRoutineDetails?.id]
  );

  const onNext = () => {
    fetchMaintenanceRoutines(paginationTokens.nextToken);
  };
  const onPrevious = () => {
    const previous =
      paginationTokens.previous?.length <= 1
        ? null
        : paginationTokens.previous?.slice(-2)[0];
    fetchMaintenanceRoutines(previous, false);
  };
  const onKeywordSubmit = (keyword) => {
    const keywordSplit = keyword && keyword.split(" ");
    fetchMaintenanceRoutines(undefined, undefined, keywordSplit);
  };
  const openModal = (action, id) =>
    setModalMaintenanceRoutine({
      visible: true,
      action,
      maintenanceRoutineId: id,
    });
  const onSuccessMaintenanceRoutine = () => {
    setModalMaintenanceRoutine({ visible: false });
    fetchMaintenanceRoutines();
  };

  async function fetchCompanyName(id) {
    try {
      const companyData = await API.graphql(
        graphqlOperation(getCompanyName, {
          id,
        })
      );
      const companyItem = companyData.data.getCompany;
      return companyItem?.name ?? "";
    } catch (err) {
      console.log("Error fetchCompanyName", err);
      return "";
    }
  }

  const getNamesByMaintenanceRoutines = (list) => {
    list?.forEach(async (item) => {
      const companyName = await fetchCompanyName(item.companyID);

      setMaintenanceRoutines((prevList) => {
        const updatedList = [...prevList];
        const index = updatedList.findIndex(
          (itemInd) => itemInd.id === item.id
        );

        updatedList[index] = {
          ...updatedList[index],
          companyName,
        };

        return updatedList;
      });
    });
  };

  async function fetchMaintenanceRoutines(token, isNext = true, keywords) {
    try {
      setMaintenanceRoutinesLoading(true);

      if (!searchBy.company.id && !me.companyID) return;

      const maintenanceRoutinesData = await API.graphql(
        graphqlOperation(maintenanceRoutinesByCompanyID, {
          companyID: searchBy.company.id || me.companyID,
          limit: !keywords ? 10 : undefined,
          nextToken: token,
          filter: keywords
            ? {
                and: keywords.map((key) => ({
                  or: [
                    {
                      name: { contains: key },
                    },
                    {
                      description: { contains: key },
                    },
                  ],
                })),
                deletedAt: { attributeExists: false },
              }
            : { deletedAt: { attributeExists: false } },
        })
      );
      const maintenanceRoutinesItems =
        maintenanceRoutinesData.data.maintenanceRoutinesByCompanyID.items;
      setMaintenanceRoutines(maintenanceRoutinesItems);
      getNamesByMaintenanceRoutines(maintenanceRoutinesItems);
      setPaginationTokens((prevSt) => {
        let previous = prevSt.previous ?? [];
        if (isNext) previous = token ? [...previous, token] : [...previous];
        if (!isNext && previous?.length) previous.pop();

        return {
          previous,
          nextToken:
            maintenanceRoutinesData.data.maintenanceRoutinesByCompanyID
              .nextToken,
        };
      });
      setMaintenanceRoutinesLoading(false);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
      setMaintenanceRoutinesLoading(false);
    } finally {
      setMaintenanceRoutinesLoading(false);
    }
  }
  async function maintenanceRoutineDelete(id) {
    try {
      const result = await API.graphql(
        graphqlOperation(updateMaintenanceRoutine, {
          input: { id, deletedAt: new Date().toISOString() },
        })
      );
      toast.success(strings.successDeleted);
      const deletedId = result.data.updateMaintenanceRoutine.id;
      setMaintenanceRoutines(
        maintenanceRoutines.filter((item) => item.id !== deletedId)
      );
      setModalDetails({
        visible: false,
        maintenanceRoutineId: null,
      });
    } catch (err) {
      toast.error(strings.responses[err]);
    }
  }

  useEffect(() => {
    fetchMaintenanceRoutines();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchBy.company.id]);

  const tableColumns = [
    {
      key: "companyName",
      label: strings.table.headers.companyName,
      _style: { width: "25%" },
    },
    {
      key: "name",
      label: strings.table.headers.name,
      _style: { width: "25%" },
    },
    {
      key: "description",
      label: strings.table.headers.description,
      _style: { width: "25%" },
    },
    {
      key: "actions",
      label: "",
      _style: { width: "25%" },
    },
  ];

  return (
    <div>
      <span className="fs-3">{strings.title}</span>
      {canCreate && (
        <Button
          style={styles.buttonAdd}
          children={strings.maintenanceRoutines.addMaintenanceRoutine}
          variation="primary"
          onClick={() => openModal("create")}
        />
      )}
      <div className="p-3">
        <Grid
          className="mb-3"
          templateColumns={{ medium: "1fr", xl: "1fr 1fr 1fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <CompanyBranchFilter
            searchBy={searchBy}
            setSearchBy={setSearchBy}
            onlyCompany
          />
          <SearchField
            label={strings.table.search.label}
            placeholder={strings.table.search.placeholder}
            onSubmit={onKeywordSubmit}
            onClear={onKeywordSubmit}
          />
        </Grid>
        {maintenanceRoutinesLoading ? (
          <Loader />
        ) : (
          <>
            <CTable
              className="table-centered"
              items={columnsMapped}
              columns={tableColumns}
              tableHeadProps={{ color: "light" }}
              responsive
              hover
            />
            <Button
              size="small"
              disabled={paginationTokens.previous?.length <= 0}
              style={styles.buttonPrevious}
              onClick={onPrevious}
            >
              <FiArrowLeft /> {strings.table.pagination.buttonPrevious}
            </Button>
            <Button
              size="small"
              disabled={!paginationTokens.nextToken}
              style={styles.buttonNext}
              onClick={onNext}
            >
              <FiArrowRight /> {strings.table.pagination.buttonNext}
            </Button>
          </>
        )}
        <CCollapse
          visible={!!maintenanceRoutineDetails?.id}
          style={styles.collapseCard}
        >
          <CCard key={maintenanceRoutineDetails?.id}>
            <CCardBody>
              <h4 className="text-muted">
                {strings.details.title} "{maintenanceRoutineDetails?.name}"
              </h4>
              <p className="text-muted">
                <b>{strings.details.description}:</b>{" "}
                {maintenanceRoutineDetails?.description}
              </p>
              <p className="text-muted">
                <b>{strings.details.createdAt}:</b>{" "}
                {moment(maintenanceRoutineDetails?.createdAt).format(
                  "YYYY-MM-DD HH:mm:ss"
                )}
              </p>
            </CCardBody>
          </CCard>
        </CCollapse>
      </div>
      <ConfirmModal
        visible={modalDetails.visible}
        body={strings.formatString(strings.modal.confirmDeleteQuestion, {
          name: modalDetails.maintenanceRoutineName,
        })}
        onCancel={() =>
          setModalDetails({ visible: false, maintenanceRoutineId: null })
        }
        onConfirm={() =>
          maintenanceRoutineDelete(modalDetails.maintenanceRoutineId)
        }
      />
      {modalMaintenanceRoutine.action && (
        <CModal
          size="xl"
          alignment="center"
          visible={modalMaintenanceRoutine.visible}
          onClose={() => setModalMaintenanceRoutine({ visible: false })}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>
              {modalMaintenanceRoutine.action === "create"
                ? strings.modalMaintenanceRoutine.addTitle
                : strings.modalMaintenanceRoutine.updateTitle}
            </CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              {modalMaintenanceRoutine.action === "create" ? (
                <MaintenanceRoutineCreateForm
                  onSuccess={onSuccessMaintenanceRoutine}
                />
              ) : (
                <MaintenanceRoutineUpdateForm
                  maintenanceRoutineId={
                    modalMaintenanceRoutine.maintenanceRoutineId
                  }
                  onSuccess={onSuccessMaintenanceRoutine}
                />
              )}
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
      {modalActs.visible && (
        <Activities
          maintenanceRoutineID={modalActs.maintenanceRoutineId}
          maintenanceRoutineName={modalActs.maintenanceRoutineName}
          visible={modalActs.visible}
          onClose={() => setModalActs({ visible: false })}
        />
      )}
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Rutinas de Mantenimiento",
    successDeleted: "Rutina de Mantenimiento eliminada con éxito",
    maintenanceRoutines: {
      addMaintenanceRoutine: "Agregar Rutina de Mantenimiento",
      show: "Ver",
      hide: "Ocultar",
      edit: "Editar",
      delete: "Eliminar",
      acts: "Actividades",
    },
    responses: {
      errorFetch: "Error al consultar rutina de mantenimiento",
    },
    table: {
      headers: {
        name: "Nombre",
        description: "Descripción",
        companyName: "Empresa",
        actions: "Acciones",
      },
      pagination: {
        buttonPrevious: "Atrás",
        buttonNext: "Siguiente",
      },
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    details: {
      title: "Detalles de Rutina de Mantenimiento",
      description: "Descripción",
      createdAt: "Fecha de creación",
    },
    modal: {
      confirmDeleteQuestion:
        "¿Estás seguro de eliminar la rutina de mantenimiento {name}?",
      cancelButton: "Cancelar",
      confirmButton: "Confirmar",
    },
    modalMaintenanceRoutine: {
      addTitle: "Agregar Rutina de Mantenimiento",
      updateTitle: "Actualizar Rutina de Mantenimiento",
    },
  },
});

const styles = {
  buttonAdd: { float: "right" },
  modal: { width: "250px" },
  collapseCard: { marginTop: "15px" },
  buttonPrevious: { marginLeft: "15px" },
  buttonNext: { float: "right", marginRight: "15px" },
  searchByKeyword: { marginBottom: "15px", width: "25%", minWidth: "300px" },
};

export default MaintenanceRoutines;
